import { useEffect, useState } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  Download,
  FileText,
  Calendar,
  CreditCard,
  Building,
  ShoppingCart,
  Check,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import type { Invoice, InvoiceLine, Product } from '../types'
import {
  formatCurrency,
  formatDate,
  getInvoiceStatusLabel,
  getInvoiceStatusColor,
} from '../utils/helpers'
import LineItemsTable from '../components/LineItemsTable'
import { useCart } from '../hooks/useCart'

export default function InvoiceDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const erpId = searchParams.get('erp_id') ? Number(searchParams.get('erp_id')) : undefined
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [addedAll, setAddedAll] = useState(false)
  const [paSubmitting, setPaSubmitting] = useState(false)
  const [paResult, setPaResult] = useState<{
    success: boolean
    message: string
    alreadySubmitted?: boolean
  } | null>(null)
  const { addToCart } = useCart()

  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? '{}')
    } catch {
      return {}
    }
  })()
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin'

  // Build a minimal Product from an invoice line for adding to cart
  const lineToProduct = (line: InvoiceLine): Product => ({
    id: line.product_id || line.product_ref || line.id || '',
    ref: line.product_ref || '',
    label: line.product_label || line.description,
    price: line.unit_price,
    price_ttc: line.unit_price * (1 + line.vat_rate / 100),
    vat_rate: line.vat_rate,
    type: 0,
    status: 1,
  })

  const handleAddLineToCart = (line: InvoiceLine, qty: number) => {
    if (!line.product_ref && !line.product_id) return
    addToCart(lineToProduct(line), qty)
  }

  const handleAddInvoiceToCart = () => {
    const lines = invoice?.lines ?? []
    lines.forEach((line) => {
      if (line.product_ref || line.product_id) {
        addToCart(lineToProduct(line), line.qty)
      }
    })
    setAddedAll(true)
    setTimeout(() => setAddedAll(false), 2000)
  }

  useEffect(() => {
    if (!id) return
    const loadInvoice = async () => {
      try {
        const data = await api.getInvoice(id, erpId)
        setInvoice(data)
      } catch (error) {
        console.error('Erreur chargement facture:', error)
      } finally {
        setLoading(false)
      }
    }
    loadInvoice()
  }, [id, erpId])

  const handleSubmitToPA = async () => {
    if (!id) return
    setPaResult(null)
    setPaSubmitting(true)
    try {
      const result = await api.submitInvoiceToPA(id, erpId)
      if (result.already_submitted) {
        setPaResult({
          success: true,
          alreadySubmitted: true,
          message: t('invoiceDetail.paAlreadySubmitted') + ' — ' + result.status,
        })
      } else {
        setPaResult({
          success: true,
          message: t('invoiceDetail.paSubmitted') + ' — ' + result.status,
        })
      }
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setPaResult({
        success: false,
        message: apiErr?.response?.data?.error ?? t('common.error'),
      })
    } finally {
      setPaSubmitting(false)
    }
  }

  const handleDownloadPDF = async () => {
    if (!id) return

    setDownloading(true)
    try {
      console.warn('Début téléchargement PDF pour facture:', id)
      const pdfData = await api.downloadInvoicePDF(id, erpId)
      console.warn('Données PDF reçues:', pdfData ? `${pdfData.substring(0, 50)}...` : 'null')
      if (pdfData) {
        const byteCharacters = atob(pdfData)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: 'application/pdf' })
        console.warn('Blob créé, taille:', blob.size)
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${invoice?.ref || 'facture'}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        console.warn('Téléchargement déclenché')
      } else {
        console.error('Pas de données PDF reçues')
        alert('Aucune donnée PDF reçue du serveur')
      }
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error)
      alert(
        'Erreur lors du téléchargement du PDF: ' +
          (error instanceof Error ? error.message : 'Erreur inconnue')
      )
    } finally {
      setDownloading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('invoiceDetail.notFound')}</h2>
        <Link to="/invoices" className="text-primary-600 hover:text-primary-700">
          {t('invoiceDetail.backToList')}
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center space-x-4">
          <Link to="/invoices" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('invoiceDetail.title', { ref: invoice.ref })}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {t('invoiceDetail.issuedOn', { date: formatDate(invoice.date) })}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {invoice?.lines && invoice.lines.some((l) => l.product_ref || l.product_id) && (
            <button
              onClick={handleAddInvoiceToCart}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${addedAll ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {addedAll ? <Check className="w-5 h-5" /> : <ShoppingCart className="w-5 h-5" />}
              <span className="hidden sm:inline">
                {addedAll ? t('catalog.addedToCart') : t('invoiceDetail.addInvoiceToCart')}
              </span>
            </button>
          )}

          {isAdmin && (
            <button
              onClick={handleSubmitToPA}
              disabled={paSubmitting}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {paSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">{t('invoiceDetail.submitToPA')}</span>
            </button>
          )}

          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">
              {downloading ? t('invoiceDetail.downloading') : t('invoiceDetail.downloadPdf')}
            </span>
          </button>
        </div>
      </div>

      {/* PA submission result — displayed below header on all screen sizes */}
      {paResult && (
        <div
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium ${
            paResult.success
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {paResult.success ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          <span>{paResult.message}</span>
        </div>
      )}

      {/* Status Badge */}
      <div>
        <span
          className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${getInvoiceStatusColor(invoice.status, invoice.is_paid)}`}
        >
          {getInvoiceStatusLabel(invoice.status, invoice.is_paid, t)}
        </span>
      </div>

      {/* Main Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            {t('invoiceDetail.generalInfo')}
          </h2>
        </div>

        <div className="p-6 space-y-5">
          {/* Customer block */}
          {(invoice.customer_name || invoice.customer_address) && (
            <div className="flex items-start gap-3 pb-5 border-b border-gray-100">
              <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building className="w-4 h-4 text-indigo-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-400 mb-0.5">{t('invoiceDetail.customer')}</p>
                {invoice.customer_name && (
                  <p className="font-semibold text-gray-900 truncate">{invoice.customer_name}</p>
                )}
                {invoice.customer_ref && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('invoiceDetail.customerRef')} : {invoice.customer_ref}
                  </p>
                )}
                {(invoice.customer_address || invoice.customer_zip || invoice.customer_city) && (
                  <p className="text-sm text-gray-500 mt-1 leading-snug">
                    {[
                      invoice.customer_address,
                      [invoice.customer_zip, invoice.customer_city].filter(Boolean).join(' '),
                      invoice.customer_country,
                    ]
                      .filter(Boolean)
                      .join(' — ')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Dates grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
            <div>
              <p className="text-xs text-gray-400 mb-1">{t('invoiceDetail.invoiceDate')}</p>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                <p className="text-sm font-medium text-gray-900">{formatDate(invoice.date)}</p>
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-400 mb-1">{t('invoiceDetail.dueDate')}</p>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                <p className="text-sm font-medium text-gray-900">{formatDate(invoice.due_date)}</p>
              </div>
            </div>

            {invoice.date_creation && (
              <div>
                <p className="text-xs text-gray-400 mb-1">{t('invoiceDetail.creationDate')}</p>
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(invoice.date_creation)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Amounts Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 flex flex-col gap-1 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('invoiceDetail.totalExclTax')}</span>
            <span className="font-medium text-gray-900">
              {formatCurrency(invoice.total_excl_tax, invoice.currency)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('invoiceDetail.vat')}</span>
            <span className="font-medium text-gray-900">
              {formatCurrency(invoice.total_incl_tax - invoice.total_excl_tax, invoice.currency)}
            </span>
          </div>
          <div className="flex items-center justify-between pt-1 border-t border-gray-100 mt-1">
            <span className="font-semibold text-gray-900">{t('invoiceDetail.totalInclTax')}</span>
            <span className="font-bold text-gray-900">
              {formatCurrency(invoice.total_incl_tax, invoice.currency)}
            </span>
          </div>
          {invoice.remaining_amount > 0 && (
            <div className="flex items-center justify-between pt-1 border-t border-amber-100 mt-1 bg-amber-50 -mx-4 px-4 py-2">
              <div className="flex items-center space-x-2">
                <CreditCard className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-amber-900">{t('invoiceDetail.remaining')}</span>
              </div>
              <span className="font-bold text-amber-900">
                {formatCurrency(invoice.remaining_amount, invoice.currency)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Line items */}
      {invoice.lines !== undefined && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">{t('lineItems.title')}</h2>
          </div>
          <div className="p-6">
            <LineItemsTable
              lines={invoice.lines ?? []}
              onAddToCart={(line, qty) => handleAddLineToCart(line as InvoiceLine, qty)}
            />
          </div>
        </div>
      )}

      {/* Notes */}
      {invoice.notes && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-700 whitespace-pre-wrap">{invoice.notes}</p>
          </div>
        </div>
      )}
    </div>
  )
}
